import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { addDays } from "date-fns";
import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialogValues } from "../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { AuditActionEnum } from "../../services/audit-reporting-data-service/Models";
import i18n from "../../services/localization-service/i18n";
import { auditReportingActionItems } from "./AuditReportingActions";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type AuditReportingStateType = {

    run: boolean;
    data: GridData | undefined;
    actions: ActionMenuItem[];
    isDirty: boolean;
    viewOptionState: ViewOptionStateEnum;
    durationLabel: string;
    durationOptions: CustomDurationDialogValues;
    auditAction?: AuditActionEnum;
    objectType?: eLynxObjectTypeEnum;
    userName?: string;
    objectName?: string;
    oldValue?: string;
    newValue?: string;
    searchText: string;
    maxRows: number;
    additionalInfo?: string;
    loading: boolean;
    downloadUid: string | undefined;
};

const defaultAuditReportingState: AuditReportingStateType = {
    run: false,
    data: undefined,
    actions: [...auditReportingActionItems],
    isDirty: false,
    viewOptionState: ViewOptionStateEnum.Default,
    durationLabel: i18n.t('customDuration.30Days'),
    durationOptions: {
        option: DurationOptionEnum.ThirtyDays,
        duration: {
            startDate: addDays(new Date(), -30),
            endDate: new Date()
        }
    },
    maxRows: 500,
    searchText: '',
    loading: false,
    downloadUid: undefined
};

export {defaultAuditReportingState}

enum AuditReportingStateActionType {
    RunChanged = 'AUDIT_REPORTING_RUN_CHANGED',
    DataChanged = 'AUDIT_REPORTING_DATA_CHANGED',
    SearchTextChanged = 'AUDIT_REPORTING_SEARCH_TEXT_CHANGED',
    ActionsChanged = 'AUDIT_REPORTING_ACTIONS_CHANGED',
    DurationChanged = 'AUDIT_REPORTING_DURATION_CHANGED',
    MaxRowsChanged = 'AUDIT_REPORTING_MAX_ROWS_CHANGED',
    ObjectTypeChanged = 'AUDIT_REPORTING_OBJECT_TYPE_CHANGED',
    ObjectNameChanged = 'AUDIT_REPORTING_OBJECT_NAME_CHANGED',
    ActionChanged = 'AUDIT_REPORTING_ACTION_CHANGED',
    UserChanged = 'AUDIT_REPORTING_USER_CHANGED',
    OldValueChanged = 'AUDIT_REPORTING_OLDVALUE_CHANGED',
    NewValueChanged = 'AUDIT_REPORTING_NEWVALUE_CHANGED',
    AdditionalInfoChanged = 'AUDIT_REPORTING_ADDITIONAL_INFO_CHANGED',
    LoadingChanged = 'AUDIT_REPORTING_LOADING_CHANGED',
    DownloadUidChanged = 'AUDIT_REPORTING_DOWNLOAD_UID_CHANGED',
    Reset = 'MANAGE_OBJECT_STATE_RESET'
}

type AuditReportingStateActions = | {
    type: AuditReportingStateActionType.DataChanged;
    payload: GridData;
} | {
    type: AuditReportingStateActionType.RunChanged;
    payload: boolean;
} | {
    type: AuditReportingStateActionType.SearchTextChanged;
    payload: string;
} | {
    type: AuditReportingStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: AuditReportingStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
} | {
    type: AuditReportingStateActionType.MaxRowsChanged;
    payload: number;
} | {
    type: AuditReportingStateActionType.ObjectTypeChanged;
    payload: eLynxObjectTypeEnum | undefined;
} | {
    type: AuditReportingStateActionType.ObjectNameChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.ActionChanged;
    payload: AuditActionEnum | undefined;
} | {
    type: AuditReportingStateActionType.UserChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.OldValueChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.NewValueChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.AdditionalInfoChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.LoadingChanged;
    payload: boolean;
} | {
    type: AuditReportingStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: AuditReportingStateActionType.Reset;
    payload: void;
};

const isDirty = (newState: AuditReportingStateType, 
                defaultState: AuditReportingStateType) => 
                newState.searchText !== defaultState.searchText ||
                newState.objectType !== defaultState.objectType ||
                newState.durationOptions.option !== defaultState.durationOptions.option ||
                newState.maxRows !== defaultState.maxRows ||
                newState.objectName !== defaultState.objectName ||
                newState.auditAction !== defaultState.auditAction ||
                newState.userName !== defaultState.userName ||
                newState.oldValue !== defaultState.oldValue ||
                newState.additionalInfo !== defaultState.additionalInfo ||
                newState.newValue !== defaultState.newValue;

function auditReportingStateReducer(state: AuditReportingStateType, action: AuditReportingStateActions): AuditReportingStateType {

    const copy = {...state};
    switch (action.type) {
        
        case AuditReportingStateActionType.DataChanged:
            copy.data = action.payload;
            return copy;

        case AuditReportingStateActionType.RunChanged:
            copy.run = action.payload;
            return copy;

        case AuditReportingStateActionType.LoadingChanged:
            copy.loading = action.payload;
            return copy;

        case AuditReportingStateActionType.SearchTextChanged:
            copy.searchText = action.payload.trim();
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;

        case AuditReportingStateActionType.MaxRowsChanged:
            copy.maxRows = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;


        case AuditReportingStateActionType.ObjectTypeChanged:
            copy.objectType = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.ObjectNameChanged:
            copy.objectName = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.ActionChanged:
            copy.auditAction = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.UserChanged:
            copy.userName = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.OldValueChanged:
            copy.oldValue = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AuditReportingStateActionType.NewValueChanged:
            copy.newValue = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;
    
        case AuditReportingStateActionType.AdditionalInfoChanged:
            copy.additionalInfo = action.payload;
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

            case AuditReportingStateActionType.DurationChanged:
            copy.durationLabel = CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions = {...action.payload};
            copy.isDirty = isDirty(copy, defaultAuditReportingState);
            copy.viewOptionState = copy.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;
    
        case AuditReportingStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case AuditReportingStateActionType.Reset:
            return {...defaultAuditReportingState};
    
        default:
            return state;
    }
}

export default function useAuditReportingState() {

    const [state, dispatch] = useReducer(auditReportingStateReducer, {...defaultAuditReportingState});

    const setData = useCallback((value: GridData) => dispatch({
        type: AuditReportingStateActionType.DataChanged,
        payload: value
    }), []);

    const setRun = useCallback((value: boolean) => dispatch({
        type: AuditReportingStateActionType.RunChanged,
        payload: value
    }), []);

    const setSearchText = useCallback((value: string) => dispatch({
        type: AuditReportingStateActionType.SearchTextChanged,
        payload: value
    }), []);


    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: AuditReportingStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: AuditReportingStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: AuditReportingStateActionType.DurationChanged,
        payload: value
    }), []);

    const setMaxRows = useCallback((value: number) => dispatch({
        type: AuditReportingStateActionType.MaxRowsChanged,
        payload: value
    }), []);


    const setObjectType = useCallback((value: eLynxObjectTypeEnum | undefined) => dispatch({
        type: AuditReportingStateActionType.ObjectTypeChanged,
        payload: value
    }), []);

    const setObjectName = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.ObjectNameChanged,
        payload: value
    }), []);

    const setAuditAction = useCallback((value: AuditActionEnum | undefined) => dispatch({
        type: AuditReportingStateActionType.ActionChanged,
        payload: value
    }), []);

    const setUserName = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.UserChanged,
        payload: value
    }), []);

    const setOldValue = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.OldValueChanged,
        payload: value
    }), []);

    const setNewValue = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.NewValueChanged,
        payload: value
    }), []);


    const setAdditionalInfo = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.AdditionalInfoChanged,
        payload: value
    }), []);


    const setLoading = useCallback((value: boolean) => dispatch({
        type: AuditReportingStateActionType.LoadingChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: AuditReportingStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AuditReportingStateActionType.Reset,
        payload: undefined
    }), []);

    return {
        state,
        setRun,
        setData,
        setDuration,
        setSearchText,
        setActions,
        setObjectName,
        setObjectType,
        setUserName,
        setAuditAction,
        setOldValue,
        setNewValue,
        setMaxRows,
        setAdditionalInfo,
        setLoading,
        setDownloadUid,
        resetState
    };
}

